export const PROVIDER_REQUEST_TIMEOUT_MS = 10_000;
export const PROVIDER_SIMULATION_TIMEOUT_MS = 15_000;

export type ProviderRequestErrorKind =
  "aborted" | "network" | "reverted" | "timeout" | "unsupported" | "unknown";

export type ProviderRequestOptions = {
  signal?: AbortSignal;
  timeoutMs?: number;
};

export type ProviderRequestErrorFormatText = Readonly<{
  unknownDetail: string;
  aborted(args: { operation: string }): string;
  network(args: { operation: string; detail: string }): string;
  reverted(args: { operation: string; detail: string }): string;
  timeout(args: { operation: string; timeoutMs: number }): string;
  unsupported(args: { operation: string; detail: string }): string;
  unknown(args: { operation: string; detail: string }): string;
}>;

export class ProviderRequestError extends Error {
  readonly detail: string;
  readonly kind: ProviderRequestErrorKind;
  readonly operation: string;
  readonly retryable: boolean;
  readonly timeoutMs?: number;

  constructor(args: {
    cause?: unknown;
    detail: string;
    kind: ProviderRequestErrorKind;
    message: string;
    operation: string;
    retryable: boolean;
    timeoutMs?: number;
  }) {
    super(args.message);
    this.name = "ProviderRequestError";
    this.detail = args.detail;
    this.kind = args.kind;
    this.operation = args.operation;
    this.retryable = args.retryable;
    if (args.timeoutMs !== undefined) this.timeoutMs = args.timeoutMs;
    if (args.cause !== undefined) this.cause = args.cause;
  }
}

type EstimateGasProvider<TInput extends object, TResult> = {
  estimateGas(input: TInput): Promise<TResult>;
};

type GetCodeProvider = {
  getCode(address: string): Promise<string>;
};

type GetBlockNumberProvider = {
  getBlockNumber(): Promise<number>;
};

type ResolveNameProvider = {
  resolveName(name: string): Promise<string | null>;
};

type LookupAddressProvider = {
  lookupAddress(address: string): Promise<string | null>;
};

const UNKNOWN_PROVIDER_ERROR_DETAIL = "Unknown provider error";
const DEFAULT_PROVIDER_REQUEST_ERROR_TEXT: ProviderRequestErrorFormatText = {
  unknownDetail: "Unknown provider error",
  aborted: ({ operation }) => `Provider ${operation} unavailable: request aborted`,
  network: ({ operation, detail }) => `Provider ${operation} unavailable: ${detail}`,
  reverted: ({ operation, detail }) => `Provider ${operation} failed: ${detail}`,
  timeout: ({ operation, timeoutMs }) =>
    `Provider ${operation} unavailable: timed out after ${timeoutMs}ms`,
  unsupported: ({ operation, detail }) => `Provider ${operation} unsupported: ${detail}`,
  unknown: ({ operation, detail }) => `Provider ${operation} failed: ${detail}`
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function pushText(parts: string[], value: unknown) {
  if (typeof value === "string" && value.trim()) {
    parts.push(value.trim());
    return;
  }

  if (typeof value === "number" || typeof value === "bigint") {
    parts.push(String(value));
  }
}

function collectProviderErrorText(
  error: unknown,
  parts: string[],
  seen: Set<unknown>,
  depth: number
) {
  if (depth > 3 || error === null || error === undefined || seen.has(error)) return;
  seen.add(error);

  if (typeof error === "string" || typeof error === "number" || typeof error === "bigint") {
    pushText(parts, error);
    return;
  }

  if (error instanceof Error) {
    pushText(parts, error.message);
  }

  if (!isRecord(error)) return;

  for (const key of ["shortMessage", "reason", "message", "code", "fault", "operation", "method"]) {
    pushText(parts, error[key]);
  }

  for (const key of ["error", "cause", "info", "data"]) {
    const nested = error[key];
    if (typeof nested === "string" || typeof nested === "number" || typeof nested === "bigint") {
      pushText(parts, nested);
    } else {
      collectProviderErrorText(nested, parts, seen, depth + 1);
    }
  }
}

export function extractProviderErrorText(error: unknown): string {
  const parts: string[] = [];
  collectProviderErrorText(error, parts, new Set<unknown>(), 0);
  const deduped = [...new Set(parts)];
  const detail = deduped.join(" | ").replace(/\s+/g, " ").trim();
  if (!detail) return UNKNOWN_PROVIDER_ERROR_DETAIL;
  return detail.length > 300 ? `${detail.slice(0, 297)}...` : detail;
}

function isAbortLike(error: unknown): boolean {
  if (!isRecord(error)) return false;
  return error.name === "AbortError" || error.code === "ABORT_ERR";
}

function includesAny(value: string, needles: readonly string[]): boolean {
  return needles.some((needle) => value.includes(needle));
}

function retryableFor(kind: ProviderRequestErrorKind): boolean {
  return kind === "aborted" || kind === "network" || kind === "timeout";
}

function providerRequestMessage(args: {
  detail: string;
  kind: ProviderRequestErrorKind;
  operation: string;
  timeoutMs?: number;
}): string {
  return formatProviderRequestErrorParts(args, DEFAULT_PROVIDER_REQUEST_ERROR_TEXT);
}

function resolveProviderRequestDetail(
  detail: string,
  text: ProviderRequestErrorFormatText
): string {
  return detail === UNKNOWN_PROVIDER_ERROR_DETAIL ? text.unknownDetail : detail;
}

function formatProviderRequestErrorParts(
  args: {
    detail: string;
    kind: ProviderRequestErrorKind;
    operation: string;
    timeoutMs?: number;
  },
  text: ProviderRequestErrorFormatText
): string {
  const detail = resolveProviderRequestDetail(args.detail, text);

  switch (args.kind) {
    case "aborted":
      return text.aborted({ operation: args.operation });
    case "network":
      return text.network({ operation: args.operation, detail });
    case "reverted":
      return text.reverted({ operation: args.operation, detail });
    case "timeout":
      return text.timeout({
        operation: args.operation,
        timeoutMs: args.timeoutMs ?? PROVIDER_REQUEST_TIMEOUT_MS
      });
    case "unsupported":
      return text.unsupported({ operation: args.operation, detail });
    case "unknown":
      return text.unknown({ operation: args.operation, detail });
  }
}

function makeProviderRequestError(args: {
  cause?: unknown;
  detail: string;
  kind: ProviderRequestErrorKind;
  operation: string;
  timeoutMs?: number;
}): ProviderRequestError {
  return new ProviderRequestError({
    cause: args.cause,
    detail: args.detail,
    kind: args.kind,
    message: providerRequestMessage(args),
    operation: args.operation,
    retryable: retryableFor(args.kind),
    ...(args.timeoutMs !== undefined ? { timeoutMs: args.timeoutMs } : {})
  });
}

export function classifyProviderRequestError(
  error: unknown,
  operation: string
): ProviderRequestError {
  if (error instanceof ProviderRequestError) return error;

  const detail = extractProviderErrorText(error);
  const normalized = detail.toLowerCase();

  if (isAbortLike(error) || includesAny(normalized, ["aborterror", "aborted"])) {
    return makeProviderRequestError({ cause: error, detail, kind: "aborted", operation });
  }

  if (includesAny(normalized, ["timeout", "timed out", "etimedout"])) {
    return makeProviderRequestError({ cause: error, detail, kind: "timeout", operation });
  }

  if (
    includesAny(normalized, [
      "-32601",
      "method not found",
      "not implemented",
      "unsupported",
      "unsupported_operation"
    ])
  ) {
    return makeProviderRequestError({ cause: error, detail, kind: "unsupported", operation });
  }

  if (
    includesAny(normalized, [
      "call_exception",
      "cannot estimate gas",
      "execution reverted",
      "insufficient funds",
      "revert"
    ])
  ) {
    return makeProviderRequestError({ cause: error, detail, kind: "reverted", operation });
  }

  if (
    includesAny(normalized, [
      "429",
      "502",
      "503",
      "504",
      "bad gateway",
      "econn",
      "enotfound",
      "failed to fetch",
      "gateway",
      "network",
      "rate limit",
      "server_error",
      "service unavailable"
    ])
  ) {
    return makeProviderRequestError({ cause: error, detail, kind: "network", operation });
  }

  return makeProviderRequestError({ cause: error, detail, kind: "unknown", operation });
}

function timeoutError(operation: string, timeoutMs: number): ProviderRequestError {
  return makeProviderRequestError({
    detail: `timed out after ${timeoutMs}ms`,
    kind: "timeout",
    operation,
    timeoutMs
  });
}

function abortError(operation: string, reason: unknown): ProviderRequestError {
  return makeProviderRequestError({
    cause: reason,
    detail: extractProviderErrorText(reason),
    kind: "aborted",
    operation
  });
}

export async function runProviderRequest<T>(
  operation: string,
  request: () => Promise<T>,
  options: ProviderRequestOptions = {}
): Promise<T> {
  const timeoutMs = options.timeoutMs ?? PROVIDER_REQUEST_TIMEOUT_MS;
  if (options.signal?.aborted) {
    throw abortError(operation, options.signal.reason);
  }

  let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
  let abortHandler: (() => void) | undefined;

  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutHandle = setTimeout(() => reject(timeoutError(operation, timeoutMs)), timeoutMs);
  });

  const abortPromise = new Promise<never>((_, reject) => {
    if (!options.signal) return;
    abortHandler = () => reject(abortError(operation, options.signal?.reason));
    options.signal.addEventListener("abort", abortHandler, { once: true });
  });

  try {
    return await Promise.race([request(), timeoutPromise, abortPromise]);
  } catch (error) {
    throw classifyProviderRequestError(error, operation);
  } finally {
    if (timeoutHandle !== undefined) clearTimeout(timeoutHandle);
    if (options.signal && abortHandler) {
      options.signal.removeEventListener("abort", abortHandler);
    }
  }
}

export function summarizeProviderRequestError(error: unknown): string {
  if (error instanceof ProviderRequestError) return error.message;
  return classifyProviderRequestError(error, "request").message;
}

export function formatProviderRequestError(
  error: unknown,
  text: ProviderRequestErrorFormatText,
  operation = "request"
): string {
  const providerError =
    error instanceof ProviderRequestError ? error : classifyProviderRequestError(error, operation);

  return formatProviderRequestErrorParts(
    {
      detail: providerError.detail,
      kind: providerError.kind,
      operation: providerError.operation,
      ...(providerError.timeoutMs !== undefined ? { timeoutMs: providerError.timeoutMs } : {})
    },
    text
  );
}

export function estimateGasWithProviderTimeout<TInput extends object, TResult>(
  provider: EstimateGasProvider<TInput, TResult>,
  input: TInput,
  options: ProviderRequestOptions = {}
): Promise<TResult> {
  return runProviderRequest(
    "estimateGas",
    () => provider.estimateGas(input),
    withSimulationTimeout(options)
  );
}

export function getCodeWithProviderTimeout(
  provider: GetCodeProvider,
  address: string,
  options: ProviderRequestOptions = {}
): Promise<string> {
  return runProviderRequest("getCode", () => provider.getCode(address), options);
}

export function getBlockNumberWithProviderTimeout(
  provider: GetBlockNumberProvider,
  options: ProviderRequestOptions = {}
): Promise<number> {
  return runProviderRequest("getBlockNumber", () => provider.getBlockNumber(), options);
}

export function resolveNameWithProviderTimeout(
  provider: ResolveNameProvider,
  name: string,
  options: ProviderRequestOptions = {}
): Promise<string | null> {
  return runProviderRequest("resolveName", () => provider.resolveName(name), options);
}

export function lookupAddressWithProviderTimeout(
  provider: LookupAddressProvider,
  address: string,
  options: ProviderRequestOptions = {}
): Promise<string | null> {
  return runProviderRequest("lookupAddress", () => provider.lookupAddress(address), options);
}

function withSimulationTimeout(options: ProviderRequestOptions): ProviderRequestOptions {
  return {
    ...options,
    timeoutMs: options.timeoutMs ?? PROVIDER_SIMULATION_TIMEOUT_MS
  };
}
